/**
 * The month, as a file an accountant can hand to the committee.
 *
 * This system has no closing of the books, and does not need one: the ledger
 * is append only by rule, so nobody can quietly change a month once it has
 * gone by, and a correction is a reversing entry with a reason and an author.
 * What a close is actually for, in a building, is producing something to hand
 * over at the meeting. That is a document, and this makes it.
 *
 * Everything here is a pure function over data the console has already read,
 * which is why it can be tested without a browser or a project. The one
 * browser-shaped thing, handing the file to the person, is at the bottom and
 * is three lines.
 */

import { formatPeriod, isRentEntry } from "@liyu/core";
import type { LedgerEntry, Period, PeriodSummary } from "@liyu/core";
import type { Expense, Unit } from "./data";
import { t } from "./strings";

export interface Month {
  period: Period;
  orgName: string;
  by: string;
  on: Date;
  entries: LedgerEntry[];
  expenses: Expense[];
  units: Unit[];
  months: { unitId: string; summary: PeriodSummary }[];
  held: number;
  /** Account uid to the door number or staff id it belongs to. */
  people: Map<string, string>;
}

/** A cell, a row, a sheet. Deliberately dull: it is about to become text. */
type Row = (string | number)[];

/**
 * A charge and a payment, out of one signed figure.
 *
 * The ledger stores one number whose sign carries its direction, which is
 * right for arithmetic and wrong for a person: an accountant reads two
 * columns, and a spreadsheet sums two columns. Splitting it here means the
 * file needs no formula to be useful.
 */
function sides(amount: number): [number | string, number | string] {
  return amount >= 0 ? [amount, ""] : ["", -amount];
}

function totals(month: Month): {
  charged: number;
  collected: number;
  owed: number;
  spent: number;
} {
  const rent = month.entries.filter(isRentEntry);
  const mine = month.months.filter((row) => row.summary.period === month.period);
  return {
    charged: rent.filter((e) => e.amount > 0).reduce((sum, e) => sum + e.amount, 0),
    collected: rent.filter((e) => e.amount < 0).reduce((sum, e) => sum - e.amount, 0),
    owed: mine.reduce((sum, row) => sum + row.summary.balance, 0),
    spent: month.expenses
      .filter((e) => !e.voided && e.period === month.period)
      .reduce((sum, e) => sum + e.amount, 0),
  };
}

/** Who wrote a row, said as a door number rather than as an account id. */
function person(month: Month, uid: string): string {
  return month.people.get(uid) ?? uid;
}

export function statement(month: Month): Row[] {
  const sum = totals(month);
  const label = formatPeriod(month.period, "latin");
  const blank: Row = [];
  const rows: Row[] = [];

  rows.push([t("statement.title")]);
  rows.push([t("statement.building"), month.orgName]);
  rows.push([t("statement.month"), label]);
  rows.push([t("statement.preparedBy"), month.by]);
  rows.push([t("statement.preparedOn"), month.on.toISOString().slice(0, 10)]);
  rows.push(blank);

  rows.push([t("statement.summary")]);
  rows.push([t("statement.charged"), sum.charged]);
  rows.push([t("statement.collected"), sum.collected]);
  rows.push([t("statement.stillOwed"), sum.owed]);
  rows.push([t("statement.spent"), sum.spent]);
  rows.push([t("statement.net"), sum.collected - sum.spent]);
  rows.push([t("statement.held"), month.held]);
  rows.push(blank);

  const byCategory = new Map<string, number>();
  for (const expense of month.expenses) {
    if (expense.voided || expense.period !== month.period) {
      continue;
    }
    byCategory.set(expense.category, (byCategory.get(expense.category) ?? 0) + expense.amount);
  }
  if (byCategory.size > 0) {
    rows.push([t("statement.costsByKind")]);
    rows.push([t("books.category"), t("books.amount")]);
    for (const [category, amount] of [...byCategory].sort((a, b) => b[1] - a[1])) {
      rows.push([t(`category.${category}` as Parameters<typeof t>[0]), amount]);
    }
    rows.push(blank);
  }

  const blocks = new Map(month.units.map((unit) => [unit.unitId, unit.block]));
  const owing = month.months
    .filter((row) => row.summary.period === month.period && row.summary.balance > 0)
    .sort((a, b) => b.summary.balance - a.summary.balance);
  if (owing.length > 0) {
    rows.push([t("statement.stillOwing")]);
    rows.push([
      t("queue.unit"),
      t("units.blockColumn"),
      t("statement.charged"),
      t("statement.collected"),
      t("units.balance"),
    ]);
    for (const row of owing) {
      rows.push([
        row.unitId,
        blocks.get(row.unitId) ?? "",
        row.summary.charged,
        row.summary.credited,
        row.summary.balance,
      ]);
    }
    rows.push(blank);
  }

  rows.push([t("statement.everyEntry")]);
  rows.push([
    t("books.date"),
    t("queue.unit"),
    t("statement.kind"),
    t("statement.charge"),
    t("statement.payment"),
    t("statement.where"),
    t("statement.who"),
    t("statement.note"),
    t("statement.corrects"),
  ]);
  for (const entry of month.entries) {
    const [charge, payment] = sides(entry.amount);
    rows.push([
      entry.date.slice(0, 10),
      entry.unitId,
      entry.type,
      charge,
      payment,
      entry.source,
      person(month, entry.createdBy),
      entry.note ?? "",
      entry.reverses ?? "",
    ]);
  }

  return rows;
}

/**
 * The rows as a file Excel opens correctly on the first try.
 *
 * Three things here are not decoration. The byte order mark is what stops
 * Excel reading Amharic as mojibake, and this file has Amharic column headings
 * whenever the console is in Amharic. The `sep=` line is what stops it putting
 * every row in one cell in the many locales whose list separator is a
 * semicolon. And the line ending is the one Excel expects.
 */
export function toCsv(rows: Row[]): string {
  const cell = (value: string | number): string => {
    const text = String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const body = rows.map((row) => row.map(cell).join(",")).join("\r\n");
  return `\ufeffsep=,\r\n${body}\r\n`;
}

/** A styled Excel-compatible workbook that opens directly in spreadsheet apps. */
export function toExcel(rows: Row[]): string {
  const html = (value: string): string =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  const width = Math.max(1, ...rows.map((row) => row.length));
  const body = rows
    .map((row, index) => {
      if (row.length === 0) {
        return `<tr class="spacer"><td colspan="${String(width)}"></td></tr>`;
      }
      if (row.length === 1) {
        return `<tr class="${index === 0 ? "title" : "section"}"><td colspan="${String(width)}">${html(String(row[0]))}</td></tr>`;
      }
      const cells = row
        .map(
          (value) =>
            `<td class="${typeof value === "number" ? "number" : ""}">${html(String(value))}</td>`,
        )
        .join("");
      return `<tr>${cells}${"<td></td>".repeat(Math.max(0, width - row.length))}</tr>`;
    })
    .join("");
  return `<!doctype html><html><head><meta charset="utf-8"><style>body{font-family:Calibri,Arial,sans-serif;color:#24303a;background:#fff}table{border-collapse:collapse;min-width:900px}td{border:1px solid #d9e0e5;padding:8px 10px;vertical-align:top}td.number{text-align:right;mso-number-format:"#,##0"}.title td{background:#273f54;color:#fff;font-size:18px;font-weight:700;padding:14px 12px}.section td{background:#dceaf6;color:#273f54;font-weight:700;border-top:2px solid #5da2da}.spacer td{border:0;height:8px;background:#fff}tr:nth-child(even):not(.title):not(.section):not(.spacer){background:#f5f9fc}</style></head><body><table>${body}</table></body></html>`;
}

/** What to call it on disk. Sorts by month, and says which building. */
export function fileName(orgName: string, period: Period): string {
  const building = orgName
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return `${building === "" ? "liyu" : building}-${period}.csv`;
}

export function excelFileName(orgName: string, period: Period): string {
  return fileName(orgName, period).replace(/\.csv$/, ".xls");
}
